import React, { useState, useEffect, useMemo } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  FiSend, FiMail, FiEye, FiUsers, FiAlertTriangle,
  FiCheckCircle, FiXCircle, FiZap, FiX, FiCode, FiEdit3,
} from 'react-icons/fi';
import { supabase } from '@/integrations/supabase/client';

/**
 * Growth Plan Phase 1.1 + Phase 2 broadcast tool.
 * Free-form email composer to send an ad-hoc campaign to a chosen segment.
 *
 * Complements the (template-locked) ReengagementCampaignPanel; use this
 * one for one-off broadcasts like "why did you leave?" or the Founding
 * Member offer, and use the re-engagement panel for the drip-worker flow.
 *
 * Segments mirror the send-broadcast-email edge function:
 *   all · active · trial · dormant · lifetime · free_limit
 * (free_limit = Phase 2 "Hot" — non-paying users who hit 5/5 this month)
 *
 * Variables the composer supports:
 *   {{first_name}} · {{name}} · {{email}} · {{unsubscribe_url}}
 *
 * Preset picker below the composer stamps the plan's exact copy in one
 * click ("why did you leave" + Founding Member) so the user doesn't have
 * to retype it.
 */

const DORMANT_DAYS = 30;

type Segment = 'all' | 'active' | 'trial' | 'dormant' | 'lifetime' | 'free_limit';

interface ProfileLite {
  email: string | null;
  first_name: string | null;
  name: string | null;
  is_in_trial: boolean | null;
  subscription_status: string | null;
  billing_type: string | null;
  last_login: string | null;
  analyses_used_this_month: number | null;
  subscription_plan_id: string | null;
}

const isTrial = (p: ProfileLite) =>
  p.is_in_trial === true || ['trial', 'trialing'].includes((p.subscription_status || '').toLowerCase());
const isLifetime = (p: ProfileLite) => (p.billing_type || '').toLowerCase() === 'lifetime';
const isActive = (p: ProfileLite) =>
  (p.subscription_status || '').toLowerCase() === 'active' && !isTrial(p) && !isLifetime(p);
const isDormant = (p: ProfileLite) => {
  if (!p.last_login) return true;
  return new Date(p.last_login).getTime() < Date.now() - DORMANT_DAYS * 86400000;
};
const isFreeLimit = (p: ProfileLite) =>
  (p.analyses_used_this_month ?? 0) >= 5
  && (!p.subscription_plan_id || ['free', 'starter'].includes(p.subscription_plan_id))
  && !['active', 'trialing', 'trial'].includes((p.subscription_status || '').toLowerCase())
  && (p.billing_type || '').toLowerCase() !== 'lifetime';

const SEGMENTS: { id: Segment; label: string; help: string; tone: string }[] = [
  { id: 'all',        label: 'All users',           help: 'Everyone with an email',                     tone: 'gray'   },
  { id: 'free_limit', label: 'Hit free limit (Hot)', help: '5/5 analyses used, not paying — HIGH intent', tone: 'orange' },
  { id: 'active',     label: 'Active (paying)',      help: 'Active paid subscription',                    tone: 'green'  },
  { id: 'trial',      label: 'In trial',             help: 'Currently in a trial',                        tone: 'cyan'   },
  { id: 'dormant',    label: 'Dormant',              help: `No login in ${DORMANT_DAYS}+ days`,           tone: 'red'    },
  { id: 'lifetime',   label: 'Lifetime',             help: 'Lifetime access',                             tone: 'purple' },
];

// Growth Plan copy presets — Phase 1.1 + Phase 2.2 + Phase 2.3.
// Body HTML matches the V3 design system used by the 10 canonical
// templates: ▸ MONO CYAN section label, black bold headline, warm
// paragraph copy, single dark CTA. Same visual grammar top-to-bottom.
const PRESETS: { id: string; label: string; segment: Segment; subject: string; body: string }[] = [
  {
    id: 'why-did-you-leave',
    label: 'Phase 1.1 — "Why did you leave?" (3 months Pro free)',
    segment: 'dormant',
    subject: '{{first_name}}, one quick question (3 free months inside)',
    body: `<div style="font-family:ui-monospace,'SF Mono',Menlo,Consolas,monospace;font-size:11px;text-transform:uppercase;letter-spacing:0.28em;color:#0891b2;margin:0 0 12px 0;font-weight:600;">▸&nbsp;One quick question</div>
<h1 style="color:#0a0e1a;font-family:-apple-system,BlinkMacSystemFont,'SF Pro Display',sans-serif;font-size:28px;font-weight:800;margin:0 0 20px 0;line-height:1.2;letter-spacing:-0.025em;">What did we get wrong, {{first_name}}?</h1>
<p style="color:#334155;font-size:16px;line-height:1.65;margin:0 0 20px 0;">You tried Snap Chart AI but didn't stick around, and I want to fix that. Can you tell me in <strong>one line</strong> — what was missing, or why you didn't come back?</p>
<div style="margin:24px 0;"><a href="{{feedback_url}}" style="display:inline-block;background:#0a0e1a;color:#ffffff;text-decoration:none;padding:14px 28px;border-radius:10px;font-weight:600;font-size:15px;letter-spacing:-0.005em;line-height:1;">Share your feedback (30 sec) →</a></div>
<div style="background:#ecfeff;border:1px solid #0891b222;color:#0891b2;border-radius:12px;padding:16px 18px;margin:20px 0;font-size:14px;line-height:1.55;">
  <strong>Thank-you for replying:</strong> 3 months of Pro on your account. No card, nothing to click.
</div>
<p style="color:#64748b;font-size:13px;line-height:1.6;">Or just hit <strong>Reply</strong> — it comes straight to my inbox. I read every message.<br>— JM, founder</p>`,
  },
  {
    id: 'founding-member',
    label: 'Phase 2.2 — Founding Member (40% off for life, limited seats)',
    segment: 'free_limit',
    subject: 'Founding member spot for you, {{first_name}}',
    body: `<div style="font-family:ui-monospace,'SF Mono',Menlo,Consolas,monospace;font-size:11px;text-transform:uppercase;letter-spacing:0.28em;color:#7c3aed;margin:0 0 12px 0;font-weight:600;">▸&nbsp;Founding member</div>
<h1 style="color:#0a0e1a;font-family:-apple-system,BlinkMacSystemFont,'SF Pro Display',sans-serif;font-size:28px;font-weight:800;margin:0 0 20px 0;line-height:1.2;letter-spacing:-0.025em;">Lock in early-user pricing.<br>Forever.</h1>
<p style="color:#334155;font-size:16px;line-height:1.65;margin:0 0 20px 0;">Hi {{first_name}},</p>
<p style="color:#334155;font-size:16px;line-height:1.65;margin:0 0 20px 0;">You are one of the first people to ever use Snap Chart AI. I want to lock you in as a <strong>Founding Member</strong>:</p>
<div style="background:#f4f5f7;border:1px solid #e5e7eb;border-radius:12px;padding:22px;margin:20px 0 28px 0;">
  <div style="margin-bottom:12px;"><span style="font-weight:700;color:#0a0e1a;font-size:15px;">✓ 40% off for life</span><br><span style="color:#64748b;font-size:14px;">Price never goes up. Not for a year, not ever.</span></div>
  <div style="margin-bottom:12px;"><span style="font-weight:700;color:#0a0e1a;font-size:15px;">✓ Limited seats</span><br><span style="color:#64748b;font-size:14px;">First-come first-served. Check the live counter on the page.</span></div>
  <div><span style="font-weight:700;color:#0a0e1a;font-size:15px;">✓ Your data comes with you</span><br><span style="color:#64748b;font-size:14px;">Discipline score, TDI history, saved analyses — everything stays as we grow.</span></div>
</div>
<div style="margin:24px 0;"><a href="https://snap-chart.ai/founding" style="display:inline-block;background:#0a0e1a;color:#ffffff;text-decoration:none;padding:14px 28px;border-radius:10px;font-weight:600;font-size:15px;letter-spacing:-0.005em;line-height:1;">See how many spots are left →</a></div>
<p style="color:#64748b;font-size:13px;line-height:1.6;">This is my thank-you for being early. Hit <strong>Reply</strong> if you have questions — I read every message.<br>— JM, founder</p>`,
  },
  {
    id: 'personal-1to1',
    label: 'Phase 2.3 — Personal 1:1 (Hot users only)',
    segment: 'free_limit',
    subject: 'quick one, {{first_name}}',
    body: `<div style="font-family:ui-monospace,'SF Mono',Menlo,Consolas,monospace;font-size:11px;text-transform:uppercase;letter-spacing:0.28em;color:#0891b2;margin:0 0 12px 0;font-weight:600;">▸&nbsp;Personal note</div>
<h1 style="color:#0a0e1a;font-family:-apple-system,BlinkMacSystemFont,'SF Pro Display',sans-serif;font-size:28px;font-weight:800;margin:0 0 20px 0;line-height:1.2;letter-spacing:-0.025em;">Quick one, {{first_name}}.</h1>
<p style="color:#334155;font-size:16px;line-height:1.65;margin:0 0 20px 0;">I see you use Snap Chart AI a lot — love that. As one of my earliest users, I'd like to set you up on <strong>Pro at the founding-member price</strong> (40% off for life).</p>
<p style="color:#334155;font-size:16px;line-height:1.65;margin:0 0 20px 0;">Want me to activate it? Just reply <strong>"yes"</strong> and I'll take care of it — nothing else needed on your side.</p>
<p style="color:#64748b;font-size:13px;line-height:1.6;">— JM<br>Founder, Snap Chart AI</p>`,
  },
];

interface Recipient {
  id: string;
  email: string;
  name: string | null;
  status: string;
  error_message: string | null;
  sent_at: string | null;
}
interface Campaign {
  id: string;
  segment: string;
  subject: string;
  body_html: string;
  status: string;
  total_recipients: number;
  sent_count: number;
  skipped_count: number;
  failed_count: number;
  created_at: string;
}

const toneChip: Record<string, string> = {
  gray:   'bg-white/5 border-white/10 text-gray-300',
  orange: 'bg-orange-500/10 border-orange-500/30 text-orange-300',
  green:  'bg-green-500/10 border-green-500/30 text-green-300',
  cyan:   'bg-cyan-500/10 border-cyan-500/30 text-cyan-300',
  red:    'bg-red-500/10 border-red-500/30 text-red-300',
  purple: 'bg-purple-500/10 border-purple-500/30 text-purple-300',
};

const BroadcastEmailPanel: React.FC = () => {
  const [segment, setSegment] = useState<Segment>('free_limit');
  const [subject, setSubject] = useState('');
  const [bodyHtml, setBodyHtml] = useState('');
  const [profiles, setProfiles] = useState<ProfileLite[]>([]);
  const [freeLimitCount, setFreeLimitCount] = useState<number | null>(null);
  const [loadingProfiles, setLoadingProfiles] = useState(true);
  const [sending, setSending] = useState(false);
  const [sendResult, setSendResult] = useState<{ ok: boolean; message: string } | null>(null);
  const [showPreview, setShowPreview] = useState(false);
  const [testEmail, setTestEmail] = useState('');
  const [campaigns, setCampaigns] = useState<Campaign[]>([]);
  const [loadingCampaigns, setLoadingCampaigns] = useState(false);
  const [expandedCampaign, setExpandedCampaign] = useState<string | null>(null);
  const [recipients, setRecipients] = useState<Record<string, Recipient[]>>({});

  // ── Fetch profiles for segment size preview ──────────────────────────
  useEffect(() => {
    (async () => {
      try {
        const { data } = await supabase
          .from('user_profiles')
          .select('email, first_name, name, is_in_trial, subscription_status, billing_type, last_login, analyses_used_this_month, subscription_plan_id')
          .not('email', 'is', null);
        setProfiles((data as any) || []);

        // Free-limit count via RPC (server has the auth-safe query).
        const { data: freeCount } = await supabase.rpc('get_free_limit_users');
        setFreeLimitCount((freeCount as any[])?.length ?? 0);
      } catch (err) {
        console.error('Failed to load profiles', err);
      } finally {
        setLoadingProfiles(false);
      }
    })();
    loadCampaigns();
  }, []);

  const loadCampaigns = async () => {
    setLoadingCampaigns(true);
    try {
      const { data } = await supabase
        .from('broadcast_campaigns')
        .select('*')
        .order('created_at', { ascending: false })
        .limit(20);
      setCampaigns((data as Campaign[]) || []);
    } finally {
      setLoadingCampaigns(false);
    }
  };

  const loadRecipients = async (campaignId: string) => {
    if (recipients[campaignId]) return;
    const { data } = await supabase
      .from('broadcast_recipients')
      .select('id, email, name, status, error_message, sent_at')
      .eq('campaign_id', campaignId)
      .order('created_at', { ascending: false })
      .limit(500);
    setRecipients(prev => ({ ...prev, [campaignId]: (data as Recipient[]) || [] }));
  };

  const segmentCounts = useMemo(() => {
    const c: Record<Segment, number> = { all: 0, active: 0, trial: 0, dormant: 0, lifetime: 0, free_limit: 0 };
    for (const p of profiles) {
      if (!p.email) continue;
      c.all++;
      if (isActive(p))   c.active++;
      if (isTrial(p))    c.trial++;
      if (isDormant(p))  c.dormant++;
      if (isLifetime(p)) c.lifetime++;
      if (isFreeLimit(p)) c.free_limit++;
    }
    // Prefer server count for free_limit (more accurate — uses same rule as RPC).
    if (freeLimitCount != null) c.free_limit = freeLimitCount;
    return c;
  }, [profiles, freeLimitCount]);

  const applyPreset = (presetId: string) => {
    const preset = PRESETS.find(p => p.id === presetId);
    if (!preset) return;
    setSubject(preset.subject);
    setBodyHtml(preset.body);
    setSegment(preset.segment);
  };

  const send = async (asTest: boolean) => {
    setSending(true);
    setSendResult(null);
    try {
      const { data: { session } } = await supabase.auth.getSession();
      const res = await fetch(
        `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/send-broadcast-email`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${session?.access_token}`,
          },
          body: JSON.stringify({
            segment,
            subject,
            bodyHtml,
            testEmail: asTest ? testEmail : undefined,
          }),
        },
      );
      const json = await res.json();
      if (!res.ok) throw new Error(json.error || `HTTP ${res.status}`);
      setSendResult({
        ok: true,
        message: asTest
          ? `Test sent to ${testEmail}.`
          : `Sent ${json.sentCount} · skipped ${json.skippedCount} · failed ${json.failedCount}.`,
      });
      if (!asTest) loadCampaigns();
    } catch (err) {
      setSendResult({ ok: false, message: (err as Error).message });
    } finally {
      setSending(false);
    }
  };

  const previewSubject = subject.replaceAll('{{first_name}}', 'Alex');
  const previewBody = bodyHtml.replaceAll('{{first_name}}', 'Alex').replaceAll('{{name}}', 'Alex').replaceAll('{{email}}', 'alex@example.com').replaceAll('{{unsubscribe_url}}', '#');

  return (
    <div className="max-w-5xl mx-auto">
      {/* Header */}
      <div className="mb-6">
        <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-cyan-400 mb-2">
          ▸ Broadcast email
        </div>
        <h2 className="text-2xl font-black text-white">Ad-hoc campaign composer.</h2>
        <p className="text-sm text-gray-500 mt-1">
          Free-form email to any segment. For "why did you leave?" · Founding Member · one-off announcements.
          <br />
          For drip/trial re-activation, use <span className="text-gray-400">Re-engagement</span> instead.
        </p>
      </div>

      {/* Presets */}
      <div className="mb-6 p-4 rounded-2xl border border-cyan-500/20 bg-cyan-500/5">
        <div className="font-mono text-[10px] uppercase tracking-widest text-cyan-400 mb-2">
          ▸ Growth plan presets
        </div>
        <div className="flex flex-wrap gap-2">
          {PRESETS.map(p => (
            <button
              key={p.id}
              onClick={() => applyPreset(p.id)}
              className="text-xs px-3 py-1.5 rounded-lg border border-white/10 bg-black/40 text-gray-200 hover:border-cyan-400/50 hover:bg-cyan-500/10 transition-colors"
            >
              <FiZap className="w-3 h-3 inline mr-1 text-cyan-400" />
              {p.label}
            </button>
          ))}
        </div>
      </div>

      {/* Composer */}
      <div className="rounded-2xl border border-white/10 bg-black/40 p-6 space-y-5">
        {/* Segment picker */}
        <div>
          <label className="block font-mono text-[10px] uppercase tracking-widest text-gray-500 mb-2">
            Segment
          </label>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
            {SEGMENTS.map(s => (
              <button
                key={s.id}
                onClick={() => setSegment(s.id)}
                className={`text-left p-3 rounded-xl border transition-colors ${
                  segment === s.id
                    ? toneChip[s.tone] + ' ring-2 ring-cyan-400/40'
                    : 'border-white/10 bg-white/[0.02] text-gray-400 hover:border-white/20'
                }`}
              >
                <div className="flex items-center justify-between gap-2">
                  <span className="text-sm font-semibold">{s.label}</span>
                  <span className="font-mono text-xs">
                    {loadingProfiles ? '…' : segmentCounts[s.id]}
                  </span>
                </div>
                <div className="text-[11px] mt-0.5 opacity-70">{s.help}</div>
              </button>
            ))}
          </div>
        </div>

        {/* Subject */}
        <div>
          <label className="block font-mono text-[10px] uppercase tracking-widest text-gray-500 mb-2">
            Subject
          </label>
          <input
            type="text"
            value={subject}
            onChange={e => setSubject(e.target.value)}
            placeholder="{{first_name}}, one quick question…"
            maxLength={200}
            className="w-full bg-black/60 border border-white/10 rounded-lg px-3 py-2.5 text-white text-sm placeholder-gray-600 focus:outline-none focus:border-cyan-400/50"
          />
          <div className="mt-1 text-[10px] font-mono text-gray-600">
            Variables: <span className="text-cyan-400">{'{{first_name}}'}</span> · <span className="text-cyan-400">{'{{name}}'}</span> · <span className="text-cyan-400">{'{{email}}'}</span>
          </div>
        </div>

        {/* Body */}
        <div>
          <div className="flex items-center justify-between mb-2">
            <label className="block font-mono text-[10px] uppercase tracking-widest text-gray-500">
              Body (HTML)
            </label>
            <button
              onClick={() => setShowPreview(true)}
              disabled={!subject || !bodyHtml}
              className="inline-flex items-center gap-1 text-xs text-cyan-400 hover:text-cyan-300 disabled:text-gray-600 disabled:cursor-not-allowed"
            >
              <FiEye className="w-3.5 h-3.5" />
              Preview
            </button>
          </div>
          <textarea
            value={bodyHtml}
            onChange={e => setBodyHtml(e.target.value)}
            placeholder="<p>Hi {{first_name}}, …</p>"
            rows={12}
            className="w-full bg-black/60 border border-white/10 rounded-lg px-3 py-2.5 text-white text-sm font-mono placeholder-gray-600 focus:outline-none focus:border-cyan-400/50 resize-y"
          />
          <div className="mt-1 text-[10px] font-mono text-gray-600">
            Also available: <span className="text-cyan-400">{'{{unsubscribe_url}}'}</span> (auto-appended) · <span className="text-cyan-400">{'{{feedback_url}}'}</span> (opens /feedback with email + token pre-filled — replies land in the Feedback panel).
          </div>
        </div>

        {/* Test send */}
        <div className="border-t border-white/5 pt-4">
          <div className="font-mono text-[10px] uppercase tracking-widest text-gray-500 mb-2">
            Test send (single address)
          </div>
          <div className="flex gap-2">
            <input
              type="email"
              value={testEmail}
              onChange={e => setTestEmail(e.target.value)}
              placeholder="you@example.com"
              className="flex-1 bg-black/60 border border-white/10 rounded-lg px-3 py-2 text-white text-sm placeholder-gray-600 focus:outline-none focus:border-cyan-400/50"
            />
            <button
              onClick={() => send(true)}
              disabled={sending || !subject || !bodyHtml || !testEmail.includes('@')}
              className="inline-flex items-center gap-1.5 px-4 py-2 rounded-lg border border-white/10 text-white text-sm hover:bg-white/5 disabled:opacity-40 disabled:cursor-not-allowed"
            >
              <FiMail className="w-3.5 h-3.5" />
              Send test
            </button>
          </div>
        </div>

        {/* Send bar */}
        <div className="border-t border-white/5 pt-4 flex items-center justify-between gap-4">
          <div className="text-xs text-gray-500 font-mono">
            About to send to <span className="text-white">{loadingProfiles ? '…' : segmentCounts[segment]}</span> user{segmentCounts[segment] === 1 ? '' : 's'} in <span className="text-white">{segment}</span>.
            <br />
            Estimated time: <span className="text-white">{Math.max(1, Math.ceil(segmentCounts[segment] * 0.6))}s</span>.
          </div>
          <button
            onClick={() => {
              if (!confirm(`Send to ${segmentCounts[segment]} user(s) in "${segment}"? This cannot be undone.`)) return;
              send(false);
            }}
            disabled={sending || !subject || !bodyHtml || segmentCounts[segment] === 0}
            className="inline-flex items-center gap-2 px-5 py-2.5 rounded-lg bg-gradient-to-r from-cyan-400 to-purple-400 text-black text-sm font-bold hover:scale-[1.02] transition-transform disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:scale-100"
          >
            <FiSend className="w-4 h-4" />
            {sending ? 'Sending…' : `Send to ${segmentCounts[segment]} users`}
          </button>
        </div>

        {sendResult && (
          <div className={`px-4 py-3 rounded-lg border font-mono text-xs ${
            sendResult.ok
              ? 'bg-green-500/5 border-green-500/30 text-green-300'
              : 'bg-red-500/5 border-red-500/30 text-red-300'
          }`}>
            {sendResult.ok ? '✓' : '✗'} {sendResult.message}
          </div>
        )}
      </div>

      {/* Campaign history */}
      <div className="mt-8">
        <div className="flex items-center justify-between mb-3">
          <div>
            <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-cyan-400 mb-1">
              ▸ History
            </div>
            <h3 className="text-lg font-bold text-white">Past broadcasts</h3>
          </div>
          <button
            onClick={loadCampaigns}
            className="text-xs text-cyan-400 hover:text-cyan-300"
          >
            Refresh
          </button>
        </div>

        {loadingCampaigns ? (
          <div className="text-center py-8 text-gray-500 font-mono text-xs">Loading…</div>
        ) : campaigns.length === 0 ? (
          <div className="text-center py-8 text-gray-500 font-mono text-xs border border-dashed border-white/10 rounded-2xl">
            No broadcasts yet.
          </div>
        ) : (
          <div className="space-y-2">
            {campaigns.map(c => (
              <div key={c.id} className="rounded-xl border border-white/10 bg-black/40 overflow-hidden">
                <button
                  onClick={() => {
                    if (expandedCampaign === c.id) {
                      setExpandedCampaign(null);
                    } else {
                      setExpandedCampaign(c.id);
                      loadRecipients(c.id);
                    }
                  }}
                  className="w-full p-4 flex items-center justify-between gap-4 hover:bg-white/[0.02] transition-colors"
                >
                  <div className="flex-1 min-w-0 text-left">
                    <div className="text-sm text-white truncate">{c.subject}</div>
                    <div className="text-[10px] font-mono uppercase tracking-widest text-gray-500 mt-1">
                      {c.segment} · {new Date(c.created_at).toLocaleString()}
                    </div>
                  </div>
                  <div className="flex items-center gap-3 text-xs font-mono">
                    <span className="text-green-400"><FiCheckCircle className="w-3 h-3 inline mr-1" />{c.sent_count}</span>
                    <span className="text-yellow-400"><FiAlertTriangle className="w-3 h-3 inline mr-1" />{c.skipped_count}</span>
                    <span className="text-red-400"><FiXCircle className="w-3 h-3 inline mr-1" />{c.failed_count}</span>
                  </div>
                </button>
                {expandedCampaign === c.id && (
                  <div className="border-t border-white/5 p-4 bg-black/60 max-h-96 overflow-y-auto">
                    {(recipients[c.id] || []).length === 0 ? (
                      <div className="text-gray-500 font-mono text-xs">No recipient rows.</div>
                    ) : (
                      <table className="w-full text-xs">
                        <thead>
                          <tr className="text-left text-gray-500 font-mono uppercase tracking-widest">
                            <th className="pb-2">Email</th>
                            <th className="pb-2">Status</th>
                            <th className="pb-2">Sent at</th>
                          </tr>
                        </thead>
                        <tbody>
                          {(recipients[c.id] || []).map(r => (
                            <tr key={r.id} className="border-t border-white/5">
                              <td className="py-1.5 text-gray-300">{r.email}</td>
                              <td className={`py-1.5 font-mono ${
                                r.status === 'sent' ? 'text-green-400' :
                                r.status === 'failed' ? 'text-red-400' :
                                r.status === 'skipped' ? 'text-yellow-400' : 'text-gray-500'
                              }`}>
                                {r.status}{r.error_message ? ` — ${r.error_message}` : ''}
                              </td>
                              <td className="py-1.5 text-gray-500 font-mono">{r.sent_at ? new Date(r.sent_at).toLocaleString() : '—'}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    )}
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Preview modal */}
      <AnimatePresence>
        {showPreview && (
          <motion.div
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/70 backdrop-blur-sm flex items-center justify-center z-50 p-4"
            onClick={() => setShowPreview(false)}
          >
            <motion.div
              initial={{ scale: 0.96 }} animate={{ scale: 1 }} exit={{ scale: 0.96 }}
              className="bg-white rounded-2xl w-full max-w-2xl max-h-[85vh] overflow-hidden flex flex-col"
              onClick={e => e.stopPropagation()}
            >
              <div className="p-4 border-b border-gray-200 flex items-center justify-between">
                <div>
                  <div className="text-xs uppercase tracking-widest text-gray-500">Preview · Alex</div>
                  <div className="font-bold text-gray-900 mt-0.5">{previewSubject}</div>
                </div>
                <button
                  onClick={() => setShowPreview(false)}
                  className="p-2 rounded-lg text-gray-400 hover:bg-gray-100"
                >
                  <FiX className="w-4 h-4" />
                </button>
              </div>
              {/* Wrap the body in the same V3 email shell the Edge Function
                  uses so the preview matches what recipients actually see:
                  branded header ▸ ai risk officer, white card, Ocean Reef
                  footer. */}
              <div className="p-6 overflow-y-auto flex-1 bg-[#f4f5f7]">
                <div style={{ maxWidth: 580, margin: '0 auto' }}>
                  <div style={{ textAlign: 'left', margin: '0 0 28px 0', padding: '0 4px' }}>
                    <span style={{ fontSize: 20, fontWeight: 800, color: '#0a0e1a', letterSpacing: '-0.02em' }}>
                      Snap Chart AI
                    </span>
                    <span style={{
                      display: 'inline-block', marginLeft: 8,
                      fontFamily: 'ui-monospace,"SF Mono",Menlo,Consolas,monospace',
                      fontSize: 10, textTransform: 'uppercase', letterSpacing: '0.25em',
                      color: '#0891b2', verticalAlign: 'middle',
                    }}>
                      ▸ ai risk officer
                    </span>
                  </div>
                  <div
                    style={{
                      background: '#ffffff',
                      border: '1px solid #e5e7eb',
                      borderRadius: 16,
                      padding: 36,
                      boxShadow: '0 1px 3px rgba(15,23,42,0.06), 0 8px 24px rgba(15,23,42,0.04)',
                      color: '#0a0e1a',
                    }}
                    dangerouslySetInnerHTML={{ __html: previewBody }}
                  />
                  <div style={{ margin: '36px 4px 0 4px', paddingTop: 20, borderTop: '1px solid #e5e7eb' }}>
                    <p style={{ color: '#64748b', fontSize: 12, lineHeight: 1.6, margin: 0 }}>
                      <strong style={{ color: '#334155' }}>Snap Chart AI</strong><br />
                      Ocean Reef, Panamá
                    </p>
                    <p style={{ color: '#94a3b8', fontSize: 12, margin: '14px 0 0 0' }}>
                      Privacy · Terms · Support
                    </p>
                  </div>
                </div>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default BroadcastEmailPanel;
